import { useState } from "react";
import { toast } from "sonner";
import { MessageSquare, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useUI } from "../context/UIContext";
import { submitConsultation } from "../lib/api";

const FIELD =
  "bg-bb-bg border-white/15 rounded-none text-bb-silver focus-visible:ring-1 focus-visible:ring-bb-gold focus-visible:border-bb-gold placeholder:text-bb-silver/30 h-12";

export const AskExpertDialog = () => {
  const { askOpen, setAskOpen } = useUI();
  const [form, setForm] = useState({ name: "", email: "", phone: "", question: "" });
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitConsultation(form);
      toast.success("Message sent. The owner will get back to you personally.");
      setForm({ name: "", email: "", phone: "", question: "" });
      setAskOpen(false);
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={askOpen} onOpenChange={setAskOpen}>
      <DialogContent
        data-testid="ask-expert-dialog"
        className="bg-bb-surface border border-white/10 rounded-none max-w-lg text-bb-silver"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-bb-gold mb-1">
            <MessageSquare className="h-5 w-5" />
            <span className="font-mono2 text-xs uppercase tracking-widest">
              Direct Line
            </span>
          </div>
          <DialogTitle className="font-display text-3xl uppercase text-bb-silver tracking-wide">
            Ask the Expert
          </DialogTitle>
          <DialogDescription className="text-bb-silver/60">
            Send a direct question and get a straight answer from the owner. No
            call center, no runaround.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4 mt-2">
          <div>
            <Label className="text-xs uppercase tracking-widest text-bb-silver/70 mb-2 block">
              Name
            </Label>
            <Input
              required
              data-testid="ask-name-input"
              value={form.name}
              onChange={upd("name")}
              placeholder="Your name"
              className={FIELD}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-bb-silver/70 mb-2 block">
                Email
              </Label>
              <Input
                required
                type="email"
                data-testid="ask-email-input"
                value={form.email}
                onChange={upd("email")}
                placeholder="you@company.com"
                className={FIELD}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-bb-silver/70 mb-2 block">
                Phone <span className="text-bb-silver/30">(optional)</span>
              </Label>
              <Input
                data-testid="ask-phone-input"
                value={form.phone}
                onChange={upd("phone")}
                placeholder="Your phone number"
                className={FIELD}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-bb-silver/70 mb-2 block">
              Your Question
            </Label>
            <Textarea
              required
              data-testid="ask-question-input"
              value={form.question}
              onChange={upd("question")}
              placeholder="What do you need help with?"
              rows={4}
              className="bg-bb-bg border-white/15 rounded-none text-bb-silver focus-visible:ring-1 focus-visible:ring-bb-gold focus-visible:border-bb-gold placeholder:text-bb-silver/30"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="ask-submit-btn"
            className="btn-sweep w-full inline-flex items-center justify-center gap-2 bg-bb-gold text-[#0a0a0a] font-cond font-bold uppercase tracking-widest py-4 hover:bg-bb-goldhi transition-colors disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Sending
              </>
            ) : (
              <span className="relative z-10">Send My Question</span>
            )}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
